import React from 'react';
import { useTranslation } from 'react-i18next';
import { initialCreatePromotion } from 'domains/promotion/Promotion.store';
import { CreatePromotionStepProps, isFlashPromotion } from 'domains/promotion/Promotion.types';
import { Flex, MarginBox, Radio, Text } from 'UI';
import { useResetScroll } from 'utils/hooks/useResetScroll';

const DiscountTypeStep = ({ setPromotion, promotion }: CreatePromotionStepProps) => {
  const { t } = useTranslation();

  useResetScroll();

  const renderFlashTypeSelector = () => {
    return (
      <Flex direction={'column'} gap={15}>
        <Radio
          disable={promotion.isSaved || promotion.editing}
          onChange={() => {
            setPromotion({
              ...promotion,
              promotionType: 'PROMOTION_FLASH_TIME_LIMITED',
              containsCustomImage: true,
              quantityLimitFlashPromotion: undefined,
            });
          }}
          checked={promotion.promotionType === 'PROMOTION_FLASH_TIME_LIMITED'}
          label={t('backoffice.promotion.promotion_flash_time_limited', 'Limited time')}
          data-cy={'promotion-flash-time-limited-selection'}
        />
        <Radio
          disable={promotion.isSaved || promotion.editing}
          onChange={() =>
            setPromotion({
              ...promotion,
              promotionType: 'PROMOTION_FLASH_QUANTITY_LIMITED',
              containsCustomImage: false,
            })
          }
          checked={promotion.promotionType === 'PROMOTION_FLASH_QUANTITY_LIMITED'}
          label={t('backoffice.promotion.promotion_flash_quantity_limited', 'Quantity limited to')}
          data-cy={'promotion-flash-quantity-limited-selection'}
        />
      </Flex>
    );
  };

  const renderFlashTypeSubtypes = () => {
    switch (promotion.promotionType) {
      case 'PROMOTION_FLASH_TIME_LIMITED':
      case 'PROMOTION_FLASH_QUANTITY_LIMITED':
      case 'PROMOTION_FLASH':
        return renderFlashTypeSelector();
      default:
        return <></>;
    }
  };

  const isDisabled = promotion.isSaved || promotion.editing || promotion.copyPromotion;

  return (
    <Flex direction={'column'} gap={50}>
      <MarginBox />
      <Radio
        disable={isDisabled}
        onChange={() => setPromotion({ ...initialCreatePromotion(), promotionType: 'DISCOUNT' })}
        checked={promotion.promotionType === 'DISCOUNT'}
        data-cy={'discount-promotion-selection'}
        label={
          <MarginBox ml={15}>
            <Flex direction={'column'}>
              <Text type={'h5_bold'}>{t('backoffice.promotion.discount', 'Discount')}</Text>
              <Text type="base">
                {t(
                  'promotion.create_new.discount.description',
                  'Directly promote items with or without visuals on Rpartstore’s website',
                )}
              </Text>
            </Flex>
          </MarginBox>
        }
      />
      <Flex direction={'column'} size={0} gap={15}>
        <Radio
          disable={isDisabled}
          data-cy={'flash-promotion-selection'}
          onChange={() => {
            if (!isFlashPromotion(promotion.promotionType)) {
              setPromotion({
                ...initialCreatePromotion(),
                promotionType: 'PROMOTION_FLASH',
              });
            }
          }}
          checked={
            promotion.promotionType === 'PROMOTION_FLASH' ||
            promotion.promotionType === 'PROMOTION_FLASH_TIME_LIMITED' ||
            promotion.promotionType === 'PROMOTION_FLASH_QUANTITY_LIMITED'
          }
          label={
            <MarginBox ml={15}>
              <Flex direction={'column'}>
                <Text type={'h5_bold'}>{t('backoffice.promotion.flash_promotion', 'Flash promotion')}</Text>
                <Text type="base">
                  {t(
                    'promotion.create_new.flash.description',
                    'Create a commercial animation with limited time or on a specific reference quantity',
                  )}
                </Text>
              </Flex>
            </MarginBox>
          }
        />
        <MarginBox ml={40}>{renderFlashTypeSubtypes()}</MarginBox>
      </Flex>
      <Radio
        disable={isDisabled}
        data-cy={'banner-promotion-selection'}
        onChange={() => setPromotion({ ...initialCreatePromotion(), promotionType: 'BANNER' })}
        checked={promotion.promotionType === 'BANNER'}
        label={
          <MarginBox ml={15}>
            <Flex direction={'column'}>
              <Text type={'h5_bold'}>{t('backoffice.promotion.banner', 'Banner')}</Text>
              <Text type="base">
                {t(
                  'promotion.create_new.banner.description',
                  'Publish communication directly on the website, with targeted options',
                )}
              </Text>
            </Flex>
          </MarginBox>
        }
      />
    </Flex>
  );
};

export default DiscountTypeStep;
